import { isBackendConversationId } from "@/components/agentic-ats/robin/utils/conversation-presenter"

/**
 * The open Robin conversation, carried in the URL alongside whatever ATS route is
 * showing. Declared once on the pathless `agent-workspace` route, which is an
 * ancestor of every ATS route, so `c` is available everywhere beneath it.
 *
 * Lives here rather than in `AgentWorkspaceRoute.tsx` so that file only exports a
 * component and keeps fast refresh.
 */
export type AgentWorkspaceSearch = { c?: string | undefined }

/**
 * Three-way on purpose, and both distinctions are load-bearing in opposite
 * directions:
 *
 *   absent          -> {}               key stays missing, so `retainSearchParams`
 *                                       on the route back-fills it across
 *                                       navigations that pass an object-form
 *                                       `search` (there are ~15 in the app, and
 *                                       object form replaces the search wholesale).
 *   present+invalid -> { c: undefined } own undefined scrubs it. `{}` would not:
 *                                       the router merges validator output OVER the
 *                                       raw search, so junk would survive.
 *   present+valid   -> { c: trimmed }
 *
 * Returning own `c: undefined` unconditionally looks tidier and is wrong — it makes
 * `"c" in result` true, which is exactly the condition `retainSearchParams` checks
 * before back-filling, so retention silently stops working.
 */
export function validateAgentWorkspaceSearch(
  search: Record<string, unknown>,
): AgentWorkspaceSearch {
  if (!("c" in search)) return {}

  // TanStack coerces numeric-looking query values, so a non-string here is junk.
  const raw = search.c
  if (typeof raw !== "string") return { c: undefined }
  const trimmed = raw.trim()
  if (!isBackendConversationId(trimmed)) return { c: undefined }
  return { c: trimmed }
}
